import { useState } from "react";
import { pluralize } from "@common/helpers/string";
import { useCancelCloseMutation, useDeleteCloseMutation } from "@api/close.api";
import { Menu } from "@design-system/Menu/Menu.lib";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { buildWorkRowUrl, parseCloseId } from "@hooks/useOpenWorkRow";
import type { ClientsWorkOverviewWorkRow } from "./clientsWorkOverviewViewConfig";

export interface ClientsWorkOverviewCallbacks {
    /** Opens the work item's detail pane, as a click on its row or card does. */
    onOpenDetails?: (row: ClientsWorkOverviewWorkRow) => void;
    /** Where View in Actions links: org Actions narrowed to the work. */
    getViewInActionsLink?: (
        row: ClientsWorkOverviewWorkRow,
    ) => string | undefined;
    /** Where Open client links: the work's workspace, narrowed to the work. */
    getOpenClientLink?: (row: ClientsWorkOverviewWorkRow) => string | undefined;
    /** Opens the work's workspace in a new tab. */
    onOpenWorkRow?: (workspaceId: string, closeId: string) => void;
    /** Opens the "Upcoming occurrences" drawer for the schedule this work was applied from. */
    onOpenOccurrencesDrawer?: (recurrenceId: string) => void;
    /** Navigates to the service template that this work was created from. */
    onGoToTemplate?: (templateId: string) => void;
}

/** A Cancel or Delete chosen from a work's actions, waiting for confirmation. */
export interface WorkRowConfirmationRequest {
    kind: "cancel" | "delete";
    /** The parsed close id the mutation takes. */
    closeId: string;
    totalTaskCount: number;
}

/**
 * The confirmations behind a work's Cancel and Delete actions. One instance
 * serves any number of rows or cards: `onConfirmationRequest` opens the
 * confirmation for the row that asked, and `confirmationModal` is the one
 * modal to render, null while nothing waits.
 */
export const useWorkRowActions = () => {
    const [request, setRequest] = useState<WorkRowConfirmationRequest | null>(
        null,
    );
    const { mutate: cancelClose } = useCancelCloseMutation();
    const { mutate: deleteClose } = useDeleteCloseMutation();
    const close = () => setRequest(null);

    const renderModal = ({
        kind,
        closeId,
        totalTaskCount,
    }: WorkRowConfirmationRequest) =>
        kind === "cancel" ? (
            <ConfirmationModal
                show
                title="Cancel this work?"
                question="Are you sure you want to cancel this work? This action can't be undone."
                yes="Yes, cancel"
                onConfirm={() => {
                    cancelClose(closeId);
                    close();
                }}
                onReject={close}
                onHide={close}
            />
        ) : (
            <ConfirmationModal
                show
                title="Delete this work?"
                question={`This will permanently delete the work and its ${totalTaskCount} ${pluralize("task", totalTaskCount)}. This action can't be undone.`}
                yes="Delete"
                onConfirm={() => {
                    deleteClose(closeId);
                    close();
                }}
                onReject={close}
                onHide={close}
            />
        );

    return {
        onConfirmationRequest: setRequest,
        confirmationModal: request && renderModal(request),
    };
};

interface WorkRowEndItemsProps {
    row: ClientsWorkOverviewWorkRow;
    onConfirmationRequest: (request: WorkRowConfirmationRequest) => void;
}

/** A work's Cancel (while it runs) and Delete, each behind its confirmation. */
export const WorkRowEndItems = ({
    row,
    onConfirmationRequest,
}: Readonly<WorkRowEndItemsProps>) => {
    const closeId = parseCloseId(row.closeId);
    if (!closeId) {
        return null;
    }
    const { totalTaskCount } = row;
    const isCancelShown = !row.isFinished && !row.cancelledAt;

    return (
        <>
            {isCancelShown && (
                <Menu.Item
                    destructive
                    onClick={() =>
                        onConfirmationRequest({
                            kind: "cancel",
                            closeId,
                            totalTaskCount,
                        })
                    }
                >
                    Cancel
                </Menu.Item>
            )}
            {/* Delete is available for all work states, including done and cancelled. */}
            <Menu.Item
                destructive
                onClick={() =>
                    onConfirmationRequest({
                        kind: "delete",
                        closeId,
                        totalTaskCount,
                    })
                }
            >
                Delete
            </Menu.Item>
        </>
    );
};

interface WorkRowActionItemsProps extends WorkRowEndItemsProps {
    callbacks: ClientsWorkOverviewCallbacks | undefined;
}

/**
 * A work's menu actions, for List's actions menu and a board card's context
 * menu: Open details, View in Actions and Open client (or Open workspace in a
 * new tab), Copy link, View occurrences (from a schedule), Go to template,
 * Cancel (while it runs) and Delete. The pane's own menu has Open client and
 * the last two.
 */
export const WorkRowActionItems = ({
    row,
    callbacks,
    onConfirmationRequest,
}: Readonly<WorkRowActionItemsProps>) => {
    const closeId = parseCloseId(row.closeId);
    if (!closeId) {
        return null;
    }
    const { recurrenceId, closeTemplateId } = row;
    const onOpenDetails = callbacks?.onOpenDetails;
    const viewInActionsLink = callbacks?.getViewInActionsLink?.(row);
    const openClientLink = callbacks?.getOpenClientLink?.(row);
    const onOpenWorkRow = callbacks?.onOpenWorkRow;
    const onOpenOccurrencesDrawer = callbacks?.onOpenOccurrencesDrawer;
    const onGoToTemplate = callbacks?.onGoToTemplate;

    return (
        <>
            {onOpenDetails && (
                <Menu.Item onClick={() => onOpenDetails(row)}>
                    Open details
                </Menu.Item>
            )}
            {viewInActionsLink && (
                <Menu.LinkItem to={viewInActionsLink}>
                    View in Actions
                </Menu.LinkItem>
            )}
            {openClientLink && (
                <Menu.LinkItem to={openClientLink}>Open client</Menu.LinkItem>
            )}
            {onOpenWorkRow && (
                <Menu.Item
                    onClick={() => onOpenWorkRow(row.workspaceId, closeId)}
                >
                    Open workspace
                </Menu.Item>
            )}
            <Menu.Item
                onClick={() => {
                    void navigator.clipboard.writeText(
                        buildWorkRowUrl(row.workspaceId, closeId),
                    );
                }}
            >
                Copy link
            </Menu.Item>
            {recurrenceId && onOpenOccurrencesDrawer && (
                <Menu.Item
                    onClick={() => onOpenOccurrencesDrawer(recurrenceId)}
                >
                    View occurrences
                </Menu.Item>
            )}
            {closeTemplateId && onGoToTemplate && (
                <Menu.Item onClick={() => onGoToTemplate(closeTemplateId)}>
                    Go to template
                </Menu.Item>
            )}
            <WorkRowEndItems
                row={row}
                onConfirmationRequest={onConfirmationRequest}
            />
        </>
    );
};
